import '.g{display:grid;}.ssr-plugin'
import '.f{gap:1.5rem;}.ssr-plugin'
import { useMemo } from 'react'
import { Title } from '@app/components/core/typography/Title'
import Row from '@app/components/layout/Row'
import Column from '@app/components/layout/Column'
import { DashboardPageLayout } from '@app/pages/layout/DashboardPageLayout'
import { useTranslations } from '@i18n/useLanguage'
import { enrich } from '@i18n/localizeHelper'
import { useUserContext } from '@app/util/hooks/useUserContext'
import { DashboardInfoCard } from '@app/components/blocks/card/DashboardInfoCard'
import { BlogCard } from '@app/components/blocks/card/BlogCard'
import { pages } from '@app/util/links'
import { useQuery } from '@based/sdk/react'
import { useLocation } from 'wouter'
import { styled } from 'inlines'
import startInitiativeImage from '@app/assets/images/consult-experts.png'
import discoverImage from '@app/assets/images/people_looking_for_house.png'
import KnowledgeHubImage from '@app/assets/images/start-initiative.png'
import { Stories } from '@schema/stories'
import useMediaQuery from '@app/util/useMediaQuery'

const STORIES_COUNT = 3

type StoryItem = Pick<Stories, 'id' | 'title' | 'slug' | 'intro' | 'images'>

export const ProfileHome = () => {
  const isMobile = useMediaQuery()
  const t = useTranslations().app
  const translations = t.dashboard.home
  const storiesLabel = t.blogPage.tabs.stories
  const { user } = useUserContext()
  const firstName = user?.firstName ?? 'Anon'
  const [, navigate] = useLocation()

  const storiesQuery = useQuery('list-stories', { top: STORIES_COUNT })
  const stories: StoryItem[] = storiesQuery.data ?? []

  const infoCards = useMemo(
    () => [
      {
        id: 'knowledgeHub',
        image: KnowledgeHubImage,
        title: translations.KnowledgeHubCardTitle,
        description: translations.KnowledgeHubCardDescription,
        buttonLabel: translations.KnowledgeHub,
        onClick: () => navigate(pages.knowledgeHub),
      },
      {
        id: 'discover',
        image: discoverImage,
        title: translations.DiscoverCardTitle,
        description: translations.DiscoverCardDescription,
        buttonLabel: translations.DiscoverMore,
        onClick: () => navigate(pages.initiatives),
      },
      {
        id: 'startInitiative',
        image: startInitiativeImage,
        title: translations.StartInitiativeCardTitle,
        description: translations.StartInitiativeCardDescription,
        buttonLabel: translations.StartInitiative,
        onClick: () => navigate(pages.dashboardStartInitiative),
      },
    ],
    [translations, navigate]
  )

  return (
    <DashboardPageLayout gap="0">
      <Title.H3 style={{ marginBottom: '1.5rem' }}>
        {enrich(translations.title, firstName)}
      </Title.H3>

      <Row
        gap="1.5rem"
        style={{
          flexWrap: 'wrap',
          alignItems: 'stretch',
          marginBottom: '2.5rem',
        }}
      >
        {infoCards.map((card) => (
          <Column key={card.id} style={{ flex: '1 1 18rem', minWidth: '16rem' }}>
            <DashboardInfoCard
              image={card.image}
              title={card.title}
              description={card.description}
              buttonLabel={card.buttonLabel}
              onClick={card.onClick}
            />
          </Column>
        ))}
      </Row>

      {stories.length > 0 && (
        <Column gap="1rem" alignItems="stretch">
          <Title.H5 style={{ margin: 0, whiteSpace: 'pre-line' }}>
            {translations.StoriesTitle}
          </Title.H5>
          <div className="g f"
            style={{
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
            }}
          >
            {stories.map((story) => (
              <BlogCard
                key={story.id}
                image={story.images?.[0]?.src ?? ''}
                meta={storiesLabel}
                title={story.title}
                intro={story.intro}
                orientation="horizontal"
                titleStyle={{ fontSize: '1.15rem' }}
                introStyle={{ fontSize: '0.875rem' }}
                onClick={() => navigate(pages.blog('verhalen', story.slug))}
              />
            ))}
          </div>
        </Column>
      )}
    </DashboardPageLayout>
  )
}
